// src/components/FilterSidebar.tsx - FIXED: Prevent metadata requests for unavailable fields
import React, { memo, useState, useCallback, useEffect, useMemo } from 'react';
import { 
  ChevronRight,
  ChevronLeft,
  X,
  Loader2,
  User,
  Building,
  Eye,
  EyeOff,
  Columns,
  RotateCcw
} from 'lucide-react';
import { useAppContext, useUIConfig } from '../hooks/useAppContext';
import { useApi } from '../hooks/useApi';
import DateRangeControl from './DateRangeControl';
import RangeSliderControl from './RangeSliderControl';
import KeywordFilterControl from './KeywordFilterControl';
import PicklistControl from './PicklistControl';
import type { FieldDefinition, DateRangeFilter, RangeSliderFilter, ExistenceFilter, DataSourceType } from '../types';
import './sidebar.css';
import './enhanced-filters.css';

interface FilterSidebarProps {
  isOpen: boolean;
  onToggle: () => void;
}

interface KeywordGroupInterface {
  mode: 'AND' | 'OR' | 'EXCLUDE';
  keywords: string[];
}

interface FilterWithValues {
  values?: string[];
}

interface FilterWithGroups {
  groups: KeywordGroupInterface[];
}

const FilterSidebar: React.FC<FilterSidebarProps> = memo(({ isOpen, onToggle }) => {
  const { state, updateFilter, clearAllFilters, clearFiltersByCategory, setColumnVisibility } = useAppContext();
  const { getMetadata, requestTrackerState } = useApi();
  const uiConfig = useUIConfig();
  
  const [activeSegment, setActiveSegment] = useState<'Contact' | 'Company'>('Contact');
  const [expandedControls, setExpandedControls] = useState<Set<string>>(new Set());
  
  const [sidebarWidth, setSidebarWidth] = useState(uiConfig.sidebarWidth);
  const [isResizing, setIsResizing] = useState(false);

  const MIN_WIDTH = Math.max(280, uiConfig.sidebarWidth - 100);
  const MAX_WIDTH = Math.min(600, uiConfig.sidebarWidth + 200);
  const COLLAPSED_WIDTH = uiConfig.sidebarCollapsedWidth;

  // Get fields for current data source
  const currentDataSource = state.searchState.dataSource;

  // Auto-set active segment based on data source
  useEffect(() => {
    if (currentDataSource === 'company' || currentDataSource === 'accounts') {
      setActiveSegment('Company');
    } else if (currentDataSource === 'people') {
      setActiveSegment('Contact');
    }
  }, [currentDataSource]);

  useEffect(() => {
    setSidebarWidth(uiConfig.sidebarWidth);
  }, [uiConfig.sidebarWidth]);

  useEffect(() => {
    const width = isOpen ? sidebarWidth : COLLAPSED_WIDTH;
    document.documentElement.style.setProperty('--sidebar-w', `${width}px`);
  }, [sidebarWidth, isOpen, COLLAPSED_WIDTH]);

  const handleResizeStart = useCallback((e: React.MouseEvent) => {
    if (isOpen) {
      setIsResizing(true);
      e.preventDefault();
    } else {
      onToggle();
    }
  }, [isOpen, onToggle]);

  const handleResizeMove = useCallback((e: MouseEvent) => {
    if (!isResizing || !isOpen) return;
    
    const newWidth = Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, e.clientX));
    setSidebarWidth(newWidth);
  }, [isResizing, isOpen, MIN_WIDTH, MAX_WIDTH]);

  const handleResizeEnd = useCallback(() => {
    setIsResizing(false);
  }, []);

  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleResizeMove);
      document.addEventListener('mouseup', handleResizeEnd);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
      
      return () => {
        document.removeEventListener('mousemove', handleResizeMove);
        document.removeEventListener('mouseup', handleResizeEnd);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      };
    }
  }, [isResizing, handleResizeMove, handleResizeEnd]);

  const getCategoryConfig = useCallback((category: 'Contact' | 'Company') => {
    const categoryConfig = state.schema?.ui_config?.categories?.[category] || {};
    return {
      icon: category === 'Contact' ? User : Building,
      label: (categoryConfig as Record<string, unknown>)?.label as string || category,
      color: (categoryConfig as Record<string, unknown>)?.color as string || '#3b82f6'
    };
  }, [state.schema]);

  const filtersStringified = useMemo(() => JSON.stringify(state.searchState.filters), [state.searchState.filters]);
  const queryString = state.searchState.query || '';
  const loadingFields = useMemo(() => requestTrackerState.loadingFields, [requestTrackerState.loadingFields]);

  // 🔧 FIXED: Add utility function to check if field is available for data source
  const isFieldAvailableForDataSource = useCallback((fieldName: string, dataSource: DataSourceType) => {
    if (!state.schema) return false;
    
    const fieldDef = state.schema.field_definitions[fieldName];
    if (!fieldDef) return false;
    
    // Check if field is contacts_only but we're requesting for people
    if (fieldDef.contacts_only && dataSource !== 'contacts') {
      return false;
    }
    
    // Check if field is actually in the data source sections
    const dataSourceConfig = state.schema.data_sources?.[dataSource];
    if (dataSourceConfig) {
      const allFields = Object.values(dataSourceConfig.sections).flat();
      return allFields.includes(fieldName);
    }
    
    // Fallback to legacy sections
    const allFields = Object.values(state.schema.sections).flat();
    return allFields.includes(fieldName);
  }, [state.schema]);

  // Get fields for current data source and category with field visibility filtering
  const getFieldsForCategory = useCallback((category: string) => {
    if (!state.schema) return [];

    // Get data source specific sections
    const dataSourceConfig = state.schema.data_sources?.[currentDataSource];
    let categoryFields: string[] = [];
    
    if (dataSourceConfig) {
      // Get fields from the specific category in the current data source
      categoryFields = dataSourceConfig.sections[category] || [];
    } else {
      // Fallback to legacy sections
      categoryFields = state.schema.sections[category] || [];
    }

    // Filter fields based on data source and field visibility
    return categoryFields.filter(fieldName => {
      const fieldDef = state.schema?.field_definitions[fieldName];
      if (!fieldDef) return false;
      
      // Hide internal fields
      if (fieldDef.internal) {
        return false;
      }
      
      // If field is contacts_only, only show it when viewing contacts
      if (fieldDef.contacts_only && currentDataSource !== 'contacts') {
        return false;
      }
      
      // Show all other fields for both data sources
      return true;
    });
  }, [state.schema, currentDataSource]);

  const getAppliedFiltersCount = useCallback((category: string) => {
    const categoryFields = getFieldsForCategory(category);
    if (!categoryFields.length) return 0;
    
    return categoryFields.reduce((count, fieldName) => {
      const value = state.searchState.filters[fieldName];
      if (!value) return count;
      
      if (typeof value === 'object' && 'type' in value) {
        if (value.type === 'existence') return count + 1;
        if (value.type === 'keyword' && 'values' in value) {
          return count + ((value as FilterWithValues).values?.length || 0);
        }
        if (value.type === 'picklist' && 'values' in value) {
          return count + ((value as FilterWithValues).values?.length || 0);
        }
        if (value.type === 'keyword_group' && 'groups' in value) {
          return count + (value as FilterWithGroups).groups.reduce((acc: number, g: KeywordGroupInterface) => acc + g.keywords.length, 0);
        }
        if (value.type === 'date_range' || value.type === 'range_slider') return count + 1;
      }
      
      if (Array.isArray(value)) return count + value.length;
      return count + 1;
    }, 0);
  }, [state.searchState.filters, getFieldsForCategory]);

  const getTotalFiltersCount = useCallback(() => {
    return getAppliedFiltersCount('Contact') + getAppliedFiltersCount('Company');
  }, [getAppliedFiltersCount]);

  // Column configuration logic (only for contacts data source)
  const columnConfigs = useMemo(() => {
    if (!state.schema || currentDataSource !== 'contacts') return { visible: [], hidden: [] };
    
    const visible = state.schema.column_configs
      .filter(col => state.visibleColumns.includes(col.id))
      .sort((a, b) => state.visibleColumns.indexOf(a.id) - state.visibleColumns.indexOf(b.id));
    
    const hidden = state.schema.column_configs
      .filter(col => !state.visibleColumns.includes(col.id));
    
    return { visible, hidden };
  }, [state.schema, state.visibleColumns, currentDataSource]);

  const toggleColumn = (columnId: string) => {
    if (!state.schema || currentDataSource !== 'contacts') return;
    
    const column = state.schema.column_configs.find(col => col.id === columnId);
    if (column?.always_visible) return;

    const newVisibleColumns = state.visibleColumns.includes(columnId)
      ? state.visibleColumns.filter(id => id !== columnId)
      : [...state.visibleColumns, columnId];

    setColumnVisibility(newVisibleColumns);
  };

  const resetColumns = () => {
    if (!state.schema?.column_configs || currentDataSource !== 'contacts') return;
    
    const defaultColumns = state.schema.column_configs
      .filter(col => col?.visible)
      .map(col => col.id);
    
    setColumnVisibility(defaultColumns);
  };

  const getFieldFilterCount = useCallback((fieldName: string) => {
    const value = state.searchState.filters[fieldName];
    if (!value) return 0;
    
    if (typeof value === 'object' && 'type' in value) {
      if (value.type === 'existence') return 1;
      if (value.type === 'keyword_group') {
        return (value as FilterWithGroups).groups.reduce((acc: number, g: KeywordGroupInterface) => acc + g.keywords.length, 0);
      }
      if (value.type === 'keyword' && 'values' in value) {
        return (value as FilterWithValues).values?.length || 0;
      }
      if (value.type === 'picklist' && 'values' in value) {
        return (value as FilterWithValues).values?.length || 0;
      }
      if (value.type === 'date_range' || value.type === 'range_slider') return 1;
    }
    
    if (Array.isArray(value)) return value.length;
    return 1;
  }, [state.searchState.filters]);

  const clearFieldFilter = useCallback((fieldName: string, event: React.MouseEvent) => {
    event.stopPropagation();
    updateFilter(fieldName, null);
  }, [updateFilter]);

  const toggleControl = useCallback((field: string) => {
    const newExpanded = new Set(expandedControls);
    
    if (newExpanded.has(field)) {
      newExpanded.delete(field);
    } else {
      newExpanded.add(field);
      
      const fieldDef = state.schema?.field_definitions[field];
      if (fieldDef?.type === 'picklist' || fieldDef?.type === 'range_slider') {
        // 🔧 FIXED: Check if field is available for current data source before requesting metadata
        if (!isFieldAvailableForDataSource(field, currentDataSource)) {

          setExpandedControls(newExpanded);
          return;
        }
        

        
        getMetadata(
          state.searchState.query || undefined,
          Object.keys(state.searchState.filters).length > 0 ? state.searchState.filters : undefined,
          [field],
          currentDataSource
        );
      }
    }
    
    setExpandedControls(newExpanded);
  }, [expandedControls, getMetadata, state.schema, state.searchState, currentDataSource, isFieldAvailableForDataSource]);

  // 🔧 FIXED: Enhanced metadata refresh effect with proper field filtering
  useEffect(() => {
    const fieldsNeedingMetadata = Array.from(expandedControls).filter(field => {
      const fieldDef = state.schema?.field_definitions[field];
      if (!fieldDef) return false;
      
      // 🔧 FIXED: Only include fields that are available for the current data source
      if (!isFieldAvailableForDataSource(field, currentDataSource)) {
        return false;
      }
      
      // Only include fields that need metadata
      return fieldDef.type === 'picklist' || fieldDef.type === 'range_slider';
    });

    if (fieldsNeedingMetadata.length > 0) {

      
      // Debug: Log any fields that were filtered out
      const filteredOutFields = Array.from(expandedControls).filter(field => {
        const fieldDef = state.schema?.field_definitions[field];
        return fieldDef && (fieldDef.type === 'picklist' || fieldDef.type === 'range_slider') && !isFieldAvailableForDataSource(field, currentDataSource);
      });
      
      if (filteredOutFields.length > 0) {

      }
      
      getMetadata(
        queryString || undefined,
        Object.keys(state.searchState.filters).length > 0 ? state.searchState.filters : undefined,
        fieldsNeedingMetadata,
        currentDataSource
      );
    }
  }, [filtersStringified, queryString, expandedControls, state.schema, getMetadata, state.searchState.filters, currentDataSource, isFieldAvailableForDataSource]);

  const renderControl = (fieldName: string, fieldDef: FieldDefinition) => {
    const isExpanded = expandedControls.has(fieldName);
    const isLoading = loadingFields.has(fieldName);
    const filterCount = getFieldFilterCount(fieldName);

    // Enhanced control rendering with new components
    if (fieldDef.type === 'keyword') {
      return (
        <div key={fieldName} className="filter-field">
          <div className={`filter-field-header ${isExpanded ? 'expanded' : ''}`}>
            <button 
              className="filter-field-toggle"
              onClick={() => toggleControl(fieldName)}
            >
              <div className="filter-field-left">
                <ChevronRight size={12} className="filter-chevron" />
                <span className="filter-field-label">{fieldDef.label}</span>
              </div>
            </button>
            <div className="filter-field-right">
              {filterCount > 0 && (
                <div className="filter-field-badge-integrated clear-specific">
                  <span className="badge-count">{filterCount}</span>
                  <div 
                    className="badge-clear"
                    onClick={(e) => clearFieldFilter(fieldName, e)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        clearFieldFilter(fieldName, e as unknown as React.MouseEvent);
                      }
                    }}
                  >
                    <X size={10} />
                  </div>
                </div>
              )}
            </div>
          </div>
          {isExpanded && (
            <div className="filter-field-content">
              <KeywordFilterControl fieldName={fieldName} />
            </div>
          )}
        </div>
      );
    }
    
    if (fieldDef.type === 'picklist') {
      return (
        <div key={fieldName} className="filter-field">
          <div className={`filter-field-header ${isExpanded ? 'expanded' : ''}`}>
            <button 
              className="filter-field-toggle"
              onClick={() => toggleControl(fieldName)}
            >
              <div className="filter-field-left">
                <ChevronRight size={12} className="filter-chevron" />
                <span className="filter-field-label">{fieldDef.label}</span>
                {isLoading && <Loader2 size={10} className="filter-loading-spinner" />}
              </div>
            </button>
            <div className="filter-field-right">
              {filterCount > 0 && (
                <div className="filter-field-badge-integrated clear-specific">
                  <span className="badge-count">{filterCount}</span>
                  <div 
                    className="badge-clear"
                    onClick={(e) => clearFieldFilter(fieldName, e)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        clearFieldFilter(fieldName, e as unknown as React.MouseEvent);
                      }
                    }}
                  >
                    <X size={10} />
                  </div>
                </div>
              )}
            </div>
          </div>
          
          {isExpanded && (
            <div className="filter-field-content">
              <PicklistControl fieldName={fieldName} isLoading={isLoading} />
            </div>
          )}
        </div>
      );
    }

    if (fieldDef.type === 'range_slider') {
      const currentValue = state.searchState.filters[fieldName] as RangeSliderFilter | ExistenceFilter | null;
      
      const fieldStats = state.metadata?.field_statistics[fieldName];
      let dynamicMin = 0;
      let dynamicMax = 1000000;

      if (fieldStats && typeof fieldStats.min_value === 'number' && typeof fieldStats.max_value === 'number') {
        dynamicMin = fieldStats.min_value;
        dynamicMax = fieldStats.max_value;
      }

      // Config-driven: disable commas for fields with no_commas set
      const noCommas = fieldDef.no_commas === true;

      return (
        <div key={fieldName} className="filter-field">
          <div className={`filter-field-header ${isExpanded ? 'expanded' : ''}`}>
            <button 
              className="filter-field-toggle"
              onClick={() => toggleControl(fieldName)}
            >
              <div className="filter-field-left">
                <ChevronRight size={12} className="filter-chevron" />
                <span className="filter-field-label">{fieldDef.label}</span>
                {isLoading && <Loader2 size={10} className="filter-loading-spinner" />}
              </div>
            </button>
            <div className="filter-field-right">
              {filterCount > 0 && (
                <div className="filter-field-badge-integrated clear-specific">
                  <span className="badge-count">{filterCount}</span>
                  <div 
                    className="badge-clear"
                    onClick={(e) => clearFieldFilter(fieldName, e)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        clearFieldFilter(fieldName, e as unknown as React.MouseEvent);
                      }
                    }}
                  >
                    <X size={10} />
                  </div>
                </div>
              )}
            </div>
          </div>
          
          {isExpanded && (
            <div className="filter-field-content">
              <RangeSliderControl
                value={currentValue}
                onChange={(value) => updateFilter(fieldName, value)}
                format={fieldDef.date_format === 'float' ? 'float' : 'integer'}
                min={dynamicMin}
                max={dynamicMax}
                noCommas={noCommas}
              />
            </div>
          )}
        </div>
      );
    }

    // Handle date range
    if (fieldDef.type === 'date_range') {
      const currentValue = state.searchState.filters[fieldName] as DateRangeFilter | ExistenceFilter | null;

      return (
        <div key={fieldName} className="filter-field">
          <div className={`filter-field-header ${isExpanded ? 'expanded' : ''}`}>
            <button 
              className="filter-field-toggle"
              onClick={() => toggleControl(fieldName)}
            >
              <div className="filter-field-left">
                <ChevronRight size={12} className="filter-chevron" />
                <span className="filter-field-label">{fieldDef.label}</span>
              </div>
            </button>
            <div className="filter-field-right">
              {filterCount > 0 && (
                <div className="filter-field-badge-integrated clear-specific">
                  <span className="badge-count">{filterCount}</span>
                  <div 
                    className="badge-clear"
                    onClick={(e) => clearFieldFilter(fieldName, e)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        clearFieldFilter(fieldName, e as unknown as React.MouseEvent);
                      }
                    }}
                  >
                    <X size={10} />
                  </div>
                </div>
              )}
            </div>
          </div>
          
          {isExpanded && (
            <div className="filter-field-content">
              <DateRangeControl
                value={currentValue}
                onChange={(value) => updateFilter(fieldName, value)}
                fieldName={fieldName}
              />
            </div>
          )}
        </div>
      );
    }

    return null;
  };

  if (!isOpen) {
    return (
      <div className="filter-sidebar-container collapsed">
        <div className="sidebar-header-collapsed">
          <button className="sidebar-collapse-btn" onClick={onToggle}>
            <ChevronRight size={uiConfig.sidebarIconSize} />
          </button>
        </div>
        
        <div className="sidebar-icons">
          {/* Show only relevant icons based on data source */}
          {(currentDataSource === 'company' ? ['Company'] : ['Contact', 'Company'] as const).map((category) => {
            const appliedCount = getAppliedFiltersCount(category);
            const categoryConfig = getCategoryConfig(category);
            const IconComponent = categoryConfig.icon;
            
            return (
              <button 
                key={category}
                className={`sidebar-icon-button ${activeSegment === category ? 'active' : ''}`}
                onClick={() => {
                  onToggle();
                  setActiveSegment(category);
                }}
                title={`${categoryConfig.label} filters${appliedCount > 0 ? ` (${appliedCount})` : ''}`}
              >
                <IconComponent size={uiConfig.sidebarIconSize} />
                {appliedCount > 0 && (
                  <span className="icon-badge">{appliedCount}</span>
                )}
              </button>
            );
          })}
        </div>

        <div 
          className="sidebar-resize-handle"
          onMouseDown={handleResizeStart}
        />
      </div>
    );
  }

  return (
    <div className={`filter-sidebar-container ${isResizing ? 'resizing' : ''} ${isOpen ? 'open' : ''}`}>
      <div className="sidebar-header">
        <div className="sidebar-header-row">
          <button className="sidebar-collapse-btn" onClick={onToggle}>
            <ChevronLeft size={18} />
          </button>
          
          {/* Only show segment control for people and contacts data sources */}
          {currentDataSource !== 'company' && currentDataSource !== 'accounts' && (
            <div className="segment-control">
              {(['Contact', 'Company'] as const).map((category) => {
                const categoryConfig = getCategoryConfig(category);
                return (
                  <button
                    key={category}
                    className={`segment-button ${activeSegment === category ? 'active' : ''}`}
                    onClick={() => setActiveSegment(category)}
                  >
                    {categoryConfig.label}
                  </button>
                );
              })}
            </div>
          )}
          
          {/* Global filter count chip */}
          {getTotalFiltersCount() > 0 && (
            <div className="global-filter-chip">
              <div className="global-filter-badge-integrated clear-all">
                <span className="badge-count">
                  {getTotalFiltersCount()}
                </span>
                <div 
                  className="badge-clear" 
                  onClick={clearAllFilters}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      clearAllFilters();
                    }
                  }}
                >
                  <X size={10} />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="sidebar-content">
        <div className="filter-category">
          <div className="category-header">
            <span className="category-title">
              <span>{activeSegment} Filters</span>
              {/* Removed data source indicator */}
              {getAppliedFiltersCount(activeSegment) > 0 && (
                <div className="category-badge-integrated clear-all">
                  <span className="badge-count">
                    {getAppliedFiltersCount(activeSegment)}
                  </span>
                  <div 
                    className="badge-clear" 
                    onClick={() => clearFiltersByCategory(activeSegment)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        clearFiltersByCategory(activeSegment);
                      }
                    }}
                  >
                    <X size={10} />
                  </div>
                </div>
              )}
            </span>
          </div>
          
          <div className="filter-fields">
            {getFieldsForCategory(activeSegment).map(fieldName => {
              const fieldDef = state.schema?.field_definitions[fieldName];
              if (!fieldDef) return null;
              return renderControl(fieldName, fieldDef);
            })}
            
            {/* Debug info for field visibility */}
            {getFieldsForCategory(activeSegment).length === 0 && (
              <div style={{ 
                padding: 16, 
                color: '#666', 
                fontSize: 12, 
                textAlign: 'center' 
              }}>
                No {activeSegment.toLowerCase()} fields available for {currentDataSource}
              </div>
            )}
          </div>
        </div>

        {/* Column Browser Section - Only for contacts data source */}
        {currentDataSource === 'contacts' && state.schema && (
          <div className="filter-category">
            <div className="category-header">
              <span className="category-title">
                <Columns size={16} style={{ marginRight: 8 }} />
                <span>Columns</span>
                <span className="category-count">
                  {state.visibleColumns.length}/{state.schema.column_configs.length}
                </span>
              </span>
            </div>
            
            <div className="filter-fields">
              {columnConfigs.visible.length > 0 && (
                <div className="columns-group">
                  <h4 className="group-title">Visible Columns</h4>
                  <div className="columns-list">
                    {columnConfigs.visible.map(column => (
                      <div key={column.id} className="column-item visible">
                        <button 
                          className="visibility-toggle"
                          onClick={() => toggleColumn(column.id)}
                          disabled={column.always_visible}
                          title={column.always_visible ? 'Always visible' : 'Hide column'}
                        >
                          <Eye size={14} />
                        </button>
                        <span className="column-label">{column.label}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {columnConfigs.hidden.length > 0 && (
                <div className="columns-group">
                  <h4 className="group-title">Hidden Columns</h4>
                  <div className="columns-list">
                    {columnConfigs.hidden.map(column => (
                      <div key={column.id} className="column-item hidden">
                        <button 
                          className="visibility-toggle"
                          onClick={() => toggleColumn(column.id)}
                          title="Show column"
                        >
                          <EyeOff size={14} />
                        </button>
                        <span className="column-label">{column.label}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              <div className="section-footer">
                <button className="section-action-button reset" onClick={resetColumns}>
                  <RotateCcw size={14} />
                  <span>Reset to Default</span>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      <div 
        className="sidebar-resize-handle"
        onMouseDown={handleResizeStart}
      />
    </div>
  );
});

FilterSidebar.displayName = 'FilterSidebar';

export default FilterSidebar;